import { useCartBooking } from '@/hooks/useCartBooking';
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { mediaUrl, formatApiError } from '@/lib/api';
import { experienceService, experienceQrUrl, experienceReceiptUrl } from '@/services/experience.service';
import { useAuth } from '@/context/AuthContext';
import { PublicLayout } from '@/components/public/PublicLayout';
import { Seo } from '@/components/Seo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';

import {
  MapPin, Clock, Minus, Plus, Ticket, CalendarDays, ShieldCheck, ArrowLeft, Info,
  CheckCircle2, Download, ImageIcon, Store, Route as RouteIcon,
  Users, Accessibility, Gauge, Car, Star, XCircle, HelpCircle, CalendarCheck, Sparkles, UserCircle2,
  ZoomIn, ChevronLeft, ChevronRight, X as XIcon,
} from 'lucide-react';
import { toast } from 'sonner';

const euro = (n) => `€${Number(n || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const SUITABLE_LABELS = { kids: 'Bambini', family: 'Family friendly', dog: 'Dog friendly', seniors: 'Terza età', all: 'Adatto a tutti' };
const ACCESS_LABELS = { wheelchair: 'Sedia a rotelle', no_barriers: 'Senza barriere', accessible_wc: 'WC accessibili', assistance: 'Assistenza dedicata', guide_dog: 'Cani guida ammessi', hearing: 'Supporto uditivo', visual: 'Supporto visivo' };
const DIFFICULTY_LABELS = { easy: 'Facile', medium: 'Media', hard: 'Difficile' };

function fmtSession(s) {
  if (!s?.start) return s?.title || 'Sessione';
  try {
    const d = new Date(s.start);
    const dd = d.toLocaleDateString('it-IT', { weekday: 'short', day: '2-digit', month: 'short' });
    const hh = d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
    return `${s.title ? s.title + ' · ' : ''}${dd} ${hh}`;
  } catch { return s.title || s.start; }
}

function Stepper({ value, onChange, min = 0, max = 99, testid }) {
  return (
    <div className="flex items-center gap-2">
      <Button type="button" variant="outline" size="icon" className="h-8 w-8" disabled={value <= min} onClick={() => onChange(Math.max(min, value - 1))} data-testid={`${testid}-minus`}><Minus className="h-4 w-4" /></Button>
      <span className="w-8 text-center text-sm font-semibold" data-testid={`${testid}-value`}>{value}</span>
      <Button type="button" variant="outline" size="icon" className="h-8 w-8" disabled={value >= max} onClick={() => onChange(Math.min(max, value + 1))} data-testid={`${testid}-plus`}><Plus className="h-4 w-4" /></Button>
    </div>
  );
}

export default function ExperiencePublicDetail() {
  const addToUnifiedCart = useCartBooking();
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isCustomer = user?.role === 'customer';

  const [exp, setExp] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [qty, setQty] = useState(1);
  const [rentalDates, setRentalDates] = useState({ start: '', end: '' }); // noleggio: ritiro/riconsegna
  const [sel, setSel] = useState({}); // tiered: {ticket_type_id: qty}
  const [courseMode, setCourseMode] = useState('full');

  
  
  const [submitting, setSubmitting] = useState(false);
  
   // {client_secret, publishable_key, order_id, currency}
  const [reviews, setReviews] = useState(null);
  const [lightbox, setLightbox] = useState(null); // { photos: string[], index: number, title: string }

  useEffect(() => {
    if (!exp?.id) return;
    (async () => {
      try {
        const r = await experienceService.publicReviews(exp.id);
        setReviews(r);
      } catch { setReviews({ items: [], count: 0 }); }
    })();
  }, [exp?.id]);

  useEffect(() => {
    (async () => {
      try {
        const e = await experienceService.publicGet(slug);
        setExp(e);
        if (e.sessions?.length) {
          const firstAvail = e.sessions.find((s) => !s.sold_out) || e.sessions[0];
          setSessionId(firstAvail?.id || '');
        }
        if (e.pricing_mode === 'course' && !(e.course?.full_price > 0) && e.course?.per_session_price > 0) setCourseMode('session');
      } catch { setNotFound(true); }
    })();
  }, [slug]);

  

  const totalQty = useMemo(() => {
    if (!exp) return 0;
    if (exp.pricing_mode === 'tiered') return Object.values(sel).reduce((a, b) => a + (b || 0), 0);
    return qty;
  }, [exp, sel, qty]);

  const isRental = exp?.pricing_mode === 'rental';
  const rentalDays = useMemo(() => {
    if (!rentalDates.start || !rentalDates.end) return 0;
    const s = new Date(rentalDates.start), e = new Date(rentalDates.end);
    const d = Math.round((e - s) / 86400000);
    return Number.isFinite(d) ? d : 0;
  }, [rentalDates]);

  const total = useMemo(() => {
    if (!exp) return 0;
    if (exp.pricing_mode === 'flat') return (exp.flat_price || 0) * qty;
    if (exp.pricing_mode === 'course') return ((courseMode === 'session' ? exp.course?.per_session_price : exp.course?.full_price) || 0) * qty;
    if (exp.pricing_mode === 'tiered') return (exp.ticket_types || []).reduce((s, t) => s + (sel[t.id] || 0) * (t.price || 0), 0);
    if (exp.pricing_mode === 'rental') return (exp.rental_price_per_day || 0) * qty * Math.max(0, rentalDays) + (exp.rental_deposit || 0) * qty;
    return 0;
  }, [exp, sel, qty, courseMode, rentalDays]);

  const selectedSession = useMemo(() => (exp?.sessions || []).find((s) => s.id === sessionId), [exp, sessionId]);
  const needsSession = !isRental && (exp?.sessions?.length || 0) > 0;

  const rentalValid = isRental && rentalDays >= (exp?.rental_min_days || 1) && rentalDays <= (exp?.rental_max_days || 365) && qty >= 1 && qty <= (exp?.rental_units || 1);

  const buildItems = () => {
    if (exp.pricing_mode === 'tiered') return (exp.ticket_types || []).filter((t) => (sel[t.id] || 0) > 0).map((t) => ({ ticket_type_id: t.id, qty: sel[t.id] }));
    return [{ qty }];
  };

  const canProceed = isRental ? rentalValid : (totalQty > 0 && (!needsSession || (sessionId && !selectedSession?.sold_out)));

  const openCheckout = async () => {
    if (!canProceed || submitting) return;
    setSubmitting(true);
    try {
      await addToUnifiedCart('experience', {
        experience_id: exp.id, session_id: needsSession ? sessionId : null,
        items: buildItems(), course_mode: courseMode,
        rental: isRental ? { start_date: rentalDates.start, end_date: rentalDates.end, qty } : null,
      });
    } catch (error) { toast.error(formatApiError(error.response?.data?.detail) || 'Impossibile aggiungere al carrello'); }
    finally { setSubmitting(false); }
  };

  // 1) crea il PaymentIntent Stripe dopo aver raccolto i dati acquirente
  

  // 2) pagamento riuscito + finalizzazione ordine
  

  if (notFound) {
    return (
      <PublicLayout>
        <div className="sila-section py-20 text-center">
          <Ticket className="mx-auto h-12 w-12 text-muted-foreground" />
          <h1 className="mt-4 font-display text-2xl font-semibold">Esperienza non trovata</h1>
          <Button className="mt-6" onClick={() => navigate('/esperienze/catalogo')}><ArrowLeft className="mr-2 h-4 w-4" />Torna al catalogo</Button>
        </div>
      </PublicLayout>
    );
  }

  if (!exp) {
    return (
      <PublicLayout>
        <div className="sila-section py-10 space-y-4"><Skeleton className="h-72 w-full rounded-2xl" /><Skeleton className="h-40 w-full rounded-2xl" /></div>
      </PublicLayout>
    );
  }

  const gallery = [exp.cover_image, ...(exp.gallery || [])].filter(Boolean).slice(0, 5);

  return (
    <PublicLayout>
      <Seo title={`${exp.title} — Sila Experiences`} description={exp.short_desc || 'Esperienza in Sila'} />

      <div className="sila-section py-8">
        <Link to="/esperienze/catalogo" className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="detail-back"><ArrowLeft className="h-4 w-4" />Catalogo esperienze</Link>

        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            {exp.category && <span className="text-xs font-medium uppercase tracking-wide text-primary">{exp.category}</span>}
            <h1 className="font-display text-3xl font-bold tracking-tight text-foreground" data-testid="detail-title">{exp.title}</h1>
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
              {exp.location?.name && <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4 text-primary" />{exp.location.name}</span>}
              {exp.duration_min > 0 && <span className="flex items-center gap-1.5"><Clock className="h-4 w-4 text-primary" />{exp.duration_min} min</span>}
              {exp.managed_by_team && <Badge className="gap-1 bg-primary/10 text-primary"><ShieldCheck className="h-3 w-3" />Gestito dal team</Badge>}
            </div>
            {exp.owner_id && exp.owner_name && (
              <Link to={`/esperienze/organizzatore/${exp.owner_id}`} className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-primary transition-colors hover:underline" data-testid="detail-organizer-link">
                <Store className="h-4 w-4" />Organizzato da {exp.owner_name}
              </Link>
            )}
          </div>
        </div>

        {/* Quick facts */}
        <div className="mt-4 flex flex-wrap gap-2" data-testid="detail-quickfacts">
          {exp.tour_duration && (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-sm text-foreground" data-testid="detail-tour-duration"><CalendarDays className="h-4 w-4 text-primary" />Durata: {exp.tour_duration}</span>
          )}
          {exp.duration_min > 0 && (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-sm text-foreground"><Clock className="h-4 w-4 text-primary" />{exp.duration_min} min</span>
          )}
          {exp.difficulty && DIFFICULTY_LABELS[exp.difficulty] && (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-sm text-foreground"><Gauge className="h-4 w-4 text-primary" />Difficoltà: {DIFFICULTY_LABELS[exp.difficulty]}</span>
          )}
          {exp.cancellation_hours > 0 && (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-sm text-foreground"><ShieldCheck className="h-4 w-4 text-primary" />Cancellazione gratuita fino a {exp.cancellation_hours}h prima</span>
          )}
          {exp.type === 'vehicle' && (exp.ticket_types || []).length > 0 && (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-sm text-foreground"><Car className="h-4 w-4 text-primary" />{exp.ticket_types.length} mezz{exp.ticket_types.length === 1 ? 'o' : 'i'} disponibil{exp.ticket_types.length === 1 ? 'e' : 'i'}</span>
          )}
          {reviews?.count > 0 && (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-sm text-foreground"><Star className="h-4 w-4 fill-amber-400 text-amber-400" />{reviews.avg_overall} · {reviews.count} recension{reviews.count === 1 ? 'e' : 'i'}</span>
          )}
        </div>

        {exp.private_tours_available && (
          <div className="mt-4 flex items-start gap-3 rounded-xl border border-primary/30 bg-primary/[0.06] p-4" data-testid="detail-private-tours">
            <Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
            <div>
              <p className="font-medium text-foreground">Tour privati su richiesta</p>
              <p className="mt-0.5 text-sm text-muted-foreground">Questo tour è disponibile anche in versione privata. Il prezzo viene concordato e comunicato in privato dall&apos;organizzatore in base alle tue esigenze.</p>
            </div>
          </div>
        )}

        {/* Gallery */}
        <div className="mt-5 grid gap-3 md:grid-cols-4 md:grid-rows-2">
          <div className="relative overflow-hidden rounded-2xl bg-muted md:col-span-2 md:row-span-2 aspect-[4/3] md:aspect-auto">
            {gallery[0] ? <img src={mediaUrl(gallery[0])} alt={exp.title} className="h-full w-full object-cover" /> : <div className="flex h-full w-full items-center justify-center"><ImageIcon className="h-10 w-10 text-muted-foreground" /></div>}
          </div>
          {gallery.slice(1, 5).map((g, i) => (
            <div key={i} className="hidden overflow-hidden rounded-2xl bg-muted aspect-[4/3] md:block"><img src={mediaUrl(g)} alt="" className="h-full w-full object-cover" /></div>
          ))}
        </div>

        <div className="mt-8 grid gap-8 lg:grid-cols-12">
          {/* Descrizione */}
          <div className="lg:col-span-7">
            <h2 className="font-display text-xl font-semibold text-foreground">Descrizione</h2>
            <p className="mt-3 whitespace-pre-line leading-relaxed text-muted-foreground">{exp.description || exp.short_desc}</p>

            {(exp.meeting_point || exp.location?.address) && (
              <div className="mt-6 rounded-xl border border-border bg-card p-4">
                <h3 className="flex items-center gap-2 font-medium text-foreground"><MapPin className="h-4 w-4 text-primary" />Dove &amp; ritrovo</h3>
                {exp.location?.address && <p className="mt-1 text-sm text-muted-foreground">{exp.location.address}</p>}
                {exp.meeting_point && <p className="mt-1 text-sm text-muted-foreground">Punto di ritrovo: {exp.meeting_point}</p>}
              </div>
            )}
            {(exp.itinerary && exp.itinerary.length > 0) && (
              <div className="mt-6" data-testid="experience-itinerary">
                <h3 className="flex items-center gap-2 font-display text-lg font-semibold text-foreground"><RouteIcon className="h-5 w-5 text-primary" />Itinerario del tour</h3>
                <ol className="mt-4 space-y-0">
                  {exp.itinerary.map((st, i) => (
                    <li key={st.id || i} className="relative flex gap-4 pb-6 last:pb-0" data-testid={`itinerary-stop-${i}`}>
                      <div className="flex flex-col items-center">
                        <span className="z-10 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">{i + 1}</span>
                        {i < exp.itinerary.length - 1 && <span className="mt-1 w-px flex-1 bg-border" aria-hidden />}
                      </div>
                      <div className="pt-1">
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                          {st.time && <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary"><Clock className="h-3.5 w-3.5" />{st.time}</span>}
                          <span className="font-semibold text-foreground">{st.title}</span>
                        </div>
                        {st.place && <p className="mt-0.5 flex items-center gap-1 text-sm text-muted-foreground"><MapPin className="h-3.5 w-3.5" />{st.place}{st.duration_min ? ` · ${st.duration_min} min` : ''}</p>}
                        {st.description && <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{st.description}</p>}
                        {(st.photos && st.photos.length > 0) && (
                          <div className="mt-2.5 grid grid-cols-3 gap-2 sm:grid-cols-4" data-testid={`itinerary-photos-${i}`}>
                            {st.photos.map((ph, k) => (
                              <button
                                key={k}
                                type="button"
                                onClick={() => setLightbox({ photos: st.photos, index: k, title: st.title || st.place || 'Tappa' })}
                                className="group/photo relative aspect-[4/3] overflow-hidden rounded-lg border border-border bg-muted outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                                aria-label={`Ingrandisci foto ${k + 1} di ${st.title || st.place || 'tappa'}`}
                                data-testid={`itinerary-photo-${i}-${k}`}
                              >
                                <img
                                  loading="lazy"
                                  src={mediaUrl(ph)}
                                  alt={`${st.title || st.place || 'Tappa'} — foto ${k + 1}`}
                                  className="h-full w-full object-cover transition-transform duration-200 group-hover/photo:scale-105"
                                />
                                <span className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/0 opacity-0 transition-all duration-200 group-hover/photo:bg-black/25 group-hover/photo:opacity-100">
                                  <ZoomIn className="h-5 w-5 text-white drop-shadow" />
                                </span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            )}
            {exp.policy_text && (
              <div className="mt-4 flex gap-2 rounded-xl border border-border bg-muted/40 p-4 text-sm text-muted-foreground">
                <Info className="mt-0.5 h-4 w-4 shrink-0 text-primary" /><span>{exp.policy_text}</span>
              </div>
            )}

            {/* Adatto a / Accessibilità */}
            {((exp.suitable_for || []).length > 0 || (exp.accessibility || []).length > 0) && (
              <div className="mt-6 grid gap-4 sm:grid-cols-2" data-testid="detail-audience">
                {(exp.suitable_for || []).length > 0 && (
                  <div className="rounded-xl border border-border bg-card p-4">
                    <h3 className="flex items-center gap-2 font-medium text-foreground"><Users className="h-4 w-4 text-primary" />Adatto a</h3>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {exp.suitable_for.map((s) => <Badge key={s} variant="outline" className="border-primary/30 text-foreground">{SUITABLE_LABELS[s] || s}</Badge>)}
                    </div>
                    {(exp.age_min != null || exp.age_max != null) && (
                      <p className="mt-2 text-xs text-muted-foreground">Età consigliata: {exp.age_min != null ? `da ${exp.age_min}` : ''}{exp.age_max != null ? ` a ${exp.age_max} anni` : (exp.age_min != null ? '+ anni' : '')}</p>
                    )}
                  </div>
                )}
                {(exp.accessibility || []).length > 0 && (
                  <div className="rounded-xl border border-border bg-card p-4">
                    <h3 className="flex items-center gap-2 font-medium text-foreground"><Accessibility className="h-4 w-4 text-primary" />Accessibilità</h3>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {exp.accessibility.map((a) => <Badge key={a} variant="outline" className="border-primary/30 text-foreground">{ACCESS_LABELS[a] || a}</Badge>)}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Cosa è incluso / non incluso */}
            {((exp.included || []).length > 0 || (exp.excluded || []).length > 0) && (
              <div className="mt-6 grid gap-4 sm:grid-cols-2" data-testid="detail-included">
                {(exp.included || []).length > 0 && (
                  <div>
                    <h3 className="flex items-center gap-2 font-display text-lg font-semibold text-foreground"><CheckCircle2 className="h-5 w-5 text-primary" />Cosa è incluso</h3>
                    <ul className="mt-3 space-y-2">
                      {exp.included.map((v, i) => <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />{v}</li>)}
                    </ul>
                  </div>
                )}
                {(exp.excluded || []).length > 0 && (
                  <div>
                    <h3 className="flex items-center gap-2 font-display text-lg font-semibold text-foreground"><XCircle className="h-5 w-5 text-muted-foreground" />Non incluso</h3>
                    <ul className="mt-3 space-y-2">
                      {exp.excluded.map((v, i) => <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground"><XCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive/70" />{v}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* La tua guida */}
            {exp.guide_note && (
              <div className="mt-6 flex gap-4 rounded-xl border border-border bg-card p-4" data-testid="detail-guide">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary"><UserCircle2 className="h-6 w-6" /></span>
                <div>
                  <h3 className="font-medium text-foreground">La tua guida</h3>
                  <p className="mt-1 whitespace-pre-line text-sm leading-relaxed text-muted-foreground">{exp.guide_note}</p>
                </div>
              </div>
            )}

            {/* Come funziona */}
            <div className="mt-6" data-testid="detail-how">
              <h3 className="flex items-center gap-2 font-display text-lg font-semibold text-foreground"><Sparkles className="h-5 w-5 text-primary" />Come funziona</h3>
              <div className="mt-3 grid gap-3 sm:grid-cols-3">
                {[
                  { icon: Ticket, t: 'Prenota online', d: 'Scegli data e biglietti/mezzi e paga in modo sicuro con carta.' },
                  { icon: CalendarCheck, t: 'Ricevi la conferma', d: 'Ottieni subito i QR code via email e nell\u2019Area Clienti.' },
                  { icon: Sparkles, t: 'Vivi l\u2019esperienza', d: 'Presentati al ritrovo e mostra il QR all\u2019ingresso.' },
                ].map((s, i) => (
                  <div key={i} className="rounded-xl border border-border bg-card p-4">
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary"><s.icon className="h-5 w-5" /></span>
                    <p className="mt-2 font-medium text-foreground">{i + 1}. {s.t}</p>
                    <p className="mt-1 text-sm text-muted-foreground">{s.d}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* FAQ */}
            {(exp.faqs || []).length > 0 && (
              <div className="mt-6" data-testid="detail-faq">
                <h3 className="flex items-center gap-2 font-display text-lg font-semibold text-foreground"><HelpCircle className="h-5 w-5 text-primary" />Domande frequenti</h3>
                <Accordion type="single" collapsible className="mt-2">
                  {exp.faqs.map((f, i) => (
                    <AccordionItem key={i} value={`faq-${i}`} data-testid={`detail-faq-${i}`}>
                      <AccordionTrigger className="text-left text-sm font-medium">{f.q}</AccordionTrigger>
                      <AccordionContent className="text-sm text-muted-foreground">{f.a}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            )}

            {/* Recensioni (legate al CRM) */}
            <div className="mt-8" data-testid="detail-reviews">
              <h3 className="flex items-center gap-2 font-display text-lg font-semibold text-foreground"><Star className="h-5 w-5 text-primary" />Recensioni</h3>
              {reviews?.count > 0 ? (
                <>
                  <div className="mt-2 flex items-center gap-3">
                    <span className="font-display text-3xl font-bold text-foreground">{reviews.avg_overall}</span>
                    <div>
                      <div className="flex" aria-label={`${reviews.avg_overall} su 5`}>
                        {[1, 2, 3, 4, 5].map((n) => <Star key={n} className={`h-4 w-4 ${n <= Math.round(reviews.avg_overall) ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground/40'}`} />)}
                      </div>
                      <p className="text-xs text-muted-foreground">{reviews.count} recension{reviews.count === 1 ? 'e' : 'i'} verificate dai clienti</p>
                    </div>
                  </div>
                  <div className="mt-4 space-y-3">
                    {reviews.items.map((r, i) => (
                      <div key={r.id || i} className="rounded-xl border border-border bg-card p-4" data-testid={`review-${i}`}>
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-foreground">{r.display_name || 'Cliente'}</span>
                          <div className="flex">{[1, 2, 3, 4, 5].map((n) => <Star key={n} className={`h-3.5 w-3.5 ${n <= Math.round(r.rating || 0) ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground/40'}`} />)}</div>
                        </div>
                        {r.comment && <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{r.comment}</p>}
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <p className="mt-2 text-sm text-muted-foreground">Ancora nessuna recensione. {isCustomer ? 'Dopo aver vissuto l\u2019esperienza potrai lasciare la tua recensione dall\u2019Area Clienti.' : 'Accedi all\u2019Area Clienti per lasciare una recensione dopo la partecipazione.'}</p>
              )}
            </div>
          </div>

          {/* Pannello acquisto */}
          <div className="lg:col-span-5">
            <Card className="sticky top-24 p-5" data-testid="booking-panel">
              <div className="flex items-baseline justify-between">
                <span className="font-display text-2xl font-bold text-foreground">{euro(exp.from_price)}</span>
                <span className="text-sm text-muted-foreground">{isRental ? 'al giorno' : 'a partire da'}</span>
              </div>
              <Separator className="my-4" />

              {needsSession && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-1.5"><CalendarDays className="h-4 w-4 text-primary" />Scegli la data</Label>
                  <Select value={sessionId} onValueChange={setSessionId}>
                    <SelectTrigger data-testid="session-select"><SelectValue placeholder="Seleziona una sessione" /></SelectTrigger>
                    <SelectContent>
                      {(exp.sessions || []).map((s) => (
                        <SelectItem key={s.id} value={s.id} disabled={s.sold_out}>
                          {fmtSession(s)}{s.sold_out ? ' · Esaurito' : (s.seats_left != null ? ` · ${s.seats_left} posti` : '')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Selettore biglietti in base al modello prezzo */}
              <div className="mt-4 space-y-3">
                {exp.pricing_mode === 'flat' && (
                  <div className="flex items-center justify-between">
                    <div><p className="text-sm font-medium text-foreground">Biglietto</p><p className="text-xs text-muted-foreground">{euro(exp.flat_price)} / persona</p></div>
                    <Stepper value={qty} onChange={setQty} min={1} testid="qty" />
                  </div>
                )}

                {exp.pricing_mode === 'course' && (
                  <>
                    <div className="grid grid-cols-1 gap-2">
                      {exp.course?.full_price > 0 && (
                        <button type="button" onClick={() => setCourseMode('full')} className={`flex items-center justify-between rounded-lg border p-3 text-left transition-colors ${courseMode === 'full' ? 'border-primary bg-primary/5' : 'border-border hover:bg-accent'}`} data-testid="course-full">
                          <span className="text-sm font-medium">Corso completo</span><span className="text-sm font-semibold text-primary">{euro(exp.course.full_price)}</span>
                        </button>
                      )}
                      {exp.course?.per_session_price > 0 && (
                        <button type="button" onClick={() => setCourseMode('session')} className={`flex items-center justify-between rounded-lg border p-3 text-left transition-colors ${courseMode === 'session' ? 'border-primary bg-primary/5' : 'border-border hover:bg-accent'}`} data-testid="course-session">
                          <span className="text-sm font-medium">Singola sessione</span><span className="text-sm font-semibold text-primary">{euro(exp.course.per_session_price)}</span>
                        </button>
                      )}
                    </div>
                    <div className="flex items-center justify-between"><p className="text-sm text-muted-foreground">Quantità</p><Stepper value={qty} onChange={setQty} min={1} testid="qty" /></div>
                  </>
                )}

                {exp.pricing_mode === 'tiered' && exp.type === 'vehicle' && (
                  <div className="space-y-2" data-testid="vehicle-tiers">
                    <p className="flex items-center gap-1.5 text-sm font-medium text-foreground"><Car className="h-4 w-4 text-primary" />Scegli il mezzo</p>
                    {(exp.ticket_types || []).map((t) => (
                      <div key={t.id} className="flex items-center gap-3 rounded-lg border border-border p-3" data-testid={`vehicle-option-${t.id}`}>
                        {t.image ? <img src={mediaUrl(t.image)} alt={t.name} className="h-14 w-16 shrink-0 rounded-md object-cover" /> : <span className="flex h-14 w-16 shrink-0 items-center justify-center rounded-md bg-muted"><Car className="h-6 w-6 text-muted-foreground" /></span>}
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-foreground">{t.name}</p>
                          <p className="text-xs text-muted-foreground">{euro(t.price)}{t.seats ? ` · ${t.seats} post${t.seats === 1 ? 'o' : 'i'}` : ''}</p>
                        </div>
                        <Stepper value={sel[t.id] || 0} onChange={(v) => setSel((s) => ({ ...s, [t.id]: v }))} min={0} testid={`tier-${t.id}`} />
                      </div>
                    ))}
                  </div>
                )}

                {exp.pricing_mode === 'tiered' && exp.type !== 'vehicle' && (
                  <div className="space-y-2" data-testid="ticket-tiers">
                    {(exp.ticket_types || []).map((t) => (
                      <div key={t.id} className="flex items-center justify-between rounded-lg border border-border p-3">
                        <div><p className="text-sm font-medium text-foreground">{t.name}</p><p className="text-xs text-muted-foreground">{euro(t.price)}{t.description ? ` · ${t.description}` : ''}</p></div>
                        <Stepper value={sel[t.id] || 0} onChange={(v) => setSel((s) => ({ ...s, [t.id]: v }))} min={0} testid={`tier-${t.id}`} />
                      </div>
                    ))}
                  </div>
                )}

                {exp.pricing_mode === 'rental' && (
                  <div className="space-y-3" data-testid="rental-booking">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label className="text-xs">Ritiro</Label>
                        <Input type="date" min={new Date().toISOString().slice(0, 10)} value={rentalDates.start}
                          onChange={(ev) => setRentalDates((d) => ({ ...d, start: ev.target.value, end: (d.end && d.end <= ev.target.value) ? '' : d.end }))}
                          data-testid="rental-start-date" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs">Riconsegna</Label>
                        <Input type="date" min={rentalDates.start || new Date().toISOString().slice(0, 10)} value={rentalDates.end}
                          disabled={!rentalDates.start}
                          onChange={(ev) => setRentalDates((d) => ({ ...d, end: ev.target.value }))}
                          data-testid="rental-end-date" />
                      </div>
                    </div>
                    <div className="flex items-center justify-between rounded-lg border border-border p-3">
                      <div><p className="text-sm font-medium text-foreground">Unità</p><p className="text-xs text-muted-foreground">{exp.rental_units} disponibil{exp.rental_units === 1 ? 'e' : 'i'} · {euro(exp.rental_price_per_day)}/giorno</p></div>
                      <Stepper value={qty} onChange={setQty} min={1} max={exp.rental_units || 1} testid="rental-qty" />
                    </div>
                    {rentalDays > 0 && (
                      <p className="text-xs text-muted-foreground" data-testid="rental-days-hint">
                        {rentalDays} giorn{rentalDays === 1 ? 'o' : 'i'} × {qty} unità × {euro(exp.rental_price_per_day)}
                        {exp.rental_deposit > 0 ? ` + cauzione ${euro(exp.rental_deposit * qty)}` : ''}
                      </p>
                    )}
                    <p className="text-[11px] leading-relaxed text-muted-foreground">Durata ammessa: {exp.rental_min_days}–{exp.rental_max_days} giorni. La cauzione è inclusa nel pagamento ed è rimborsabile alla riconsegna secondo la policy dell'operatore.</p>
                  </div>
                )}
              </div>

              <Separator className="my-4" />
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{isRental ? `Totale (${qty} unità · ${rentalDays || 0} giorn${rentalDays === 1 ? 'o' : 'i'}${exp.rental_deposit > 0 ? ', cauzione incl.' : ''})` : `Totale (${totalQty} ${exp.type === 'vehicle' ? (totalQty === 1 ? 'mezzo' : 'mezzi') : (totalQty === 1 ? 'biglietto' : 'biglietti')})`}</span>
                <span className="font-display text-xl font-bold text-foreground" data-testid="booking-total">{euro(total)}</span>
              </div>
              <Button className="mt-4 w-full" size="lg" disabled={!canProceed} onClick={openCheckout} data-testid="buy-button">
                <Ticket className="mr-2 h-4 w-4" />Acquista e paga
              </Button>
              <p className="mt-2 flex items-center justify-center gap-1.5 text-center text-xs text-muted-foreground"><ShieldCheck className="h-3.5 w-3.5" />Pagamento sicuro con carta · Stripe</p>
            </Card>
          </div>
        </div>
      </div>

      {/* Checkout dialog */}
      

      {/* Lightbox galleria tappe del tour */}
      <Dialog open={!!lightbox} onOpenChange={(o) => { if (!o) setLightbox(null); }}>
        <DialogContent className="max-w-3xl overflow-hidden border-border bg-background/95 p-0" data-testid="itinerary-lightbox">
          {lightbox && (
            <div className="relative">
              <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
                <p className="truncate text-sm font-medium text-foreground">{lightbox.title} · {lightbox.index + 1}/{lightbox.photos.length}</p>
                <button type="button" onClick={() => setLightbox(null)} className="flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted" aria-label="Chiudi" data-testid="lightbox-close">
                  <XIcon className="h-4 w-4" />
                </button>
              </div>
              <div className="relative flex items-center justify-center bg-black/90">
                <img
                  src={mediaUrl(lightbox.photos[lightbox.index])}
                  alt={`${lightbox.title} — foto ${lightbox.index + 1}`}
                  className="max-h-[70vh] w-full object-contain"
                  data-testid="lightbox-image"
                />
                {lightbox.photos.length > 1 && (
                  <>
                    <button type="button"
                      onClick={() => setLightbox((lb) => ({ ...lb, index: (lb.index - 1 + lb.photos.length) % lb.photos.length }))}
                      className="absolute left-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-background/80 text-foreground shadow-md backdrop-blur transition-colors hover:bg-background"
                      aria-label="Foto precedente" data-testid="lightbox-prev">
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                    <button type="button"
                      onClick={() => setLightbox((lb) => ({ ...lb, index: (lb.index + 1) % lb.photos.length }))}
                      className="absolute right-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-background/80 text-foreground shadow-md backdrop-blur transition-colors hover:bg-background"
                      aria-label="Foto successiva" data-testid="lightbox-next">
                      <ChevronRight className="h-5 w-5" />
                    </button>
                  </>
                )}
              </div>
              {lightbox.photos.length > 1 && (
                <div className="flex gap-2 overflow-x-auto border-t border-border p-3" data-testid="lightbox-thumbs">
                  {lightbox.photos.map((ph, k) => (
                    <button key={k} type="button" onClick={() => setLightbox((lb) => ({ ...lb, index: k }))}
                      className={`h-14 w-20 shrink-0 overflow-hidden rounded-md border-2 transition-colors ${k === lightbox.index ? 'border-primary' : 'border-transparent opacity-70 hover:opacity-100'}`}
                      aria-label={`Vai alla foto ${k + 1}`} data-testid={`lightbox-thumb-${k}`}>
                      <img src={mediaUrl(ph)} alt="" className="h-full w-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </PublicLayout>
  );
}

